import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, KeyRound, User } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'

async function fetchProfile(userId: string) {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('full_name, is_super_user')
    .eq('id', userId)
    .single()
  if (error) throw error
  return data
}

const TABS = [
  { value: 'profile', label: 'اطلاعات شخصی' },
  { value: 'password', label: 'تغییر رمز عبور' },
  { value: 'preferences', label: 'ترجیحات' },
]

export function ProfilePage() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()

  const [fullName, setFullName] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [defaultCalendar, setDefaultCalendar] = useState('jalali')

  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: () => fetchProfile(user!.id),
    enabled: !!user?.id,
  })

  useEffect(() => {
    if (profile) setFullName(profile.full_name ?? '')
  }, [profile])

  const saveProfileMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('user_profiles')
        .update({ full_name: fullName })
        .eq('id', user!.id)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-profile', user?.id] })
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: async () => {
      setPasswordError('')
      if (newPassword !== confirmPassword) throw new Error('رمزهای عبور جدید یکسان نیستند')
      if (newPassword.length < 6) throw new Error('رمز عبور باید حداقل ۶ کاراکتر باشد')
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
    },
    onSuccess: () => {
      setPasswordSuccess('رمز عبور با موفقیت تغییر یافت')
      setNewPassword('')
      setConfirmPassword('')
    },
    onError: (e: Error) => setPasswordError(e.message),
  })

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="حساب کاربری" breadcrumbs={[{ label: 'حساب کاربری' }]} />

      <Tabs defaultValue="profile" className="flex flex-col flex-1" dir="rtl">
        {/* Tab bar — full-width underline style */}
        <div className="overflow-x-auto border-b border-border shrink-0 px-6">
          <TabsList className="w-max h-auto rounded-none bg-transparent border-0 gap-0 px-0">
            {TABS.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-xs px-4 py-2.5 whitespace-nowrap"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-xl">

            <TabsContent value="profile" dir="rtl" className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <span className="text-primary-foreground text-xl font-semibold">
                    {fullName ? fullName.charAt(0) : <User className="w-6 h-6" />}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{fullName || 'کاربر'}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  {profile?.is_super_user && (
                    <span className="text-xs bg-purple-500/15 text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded-full">
                      Super User
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>نام کامل</Label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>ایمیل</Label>
                <Input value={user?.email ?? ''} disabled dir="ltr" className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground">ایمیل قابل تغییر نیست</p>
              </div>
              <Button
                onClick={() => saveProfileMutation.mutate()}
                disabled={saveProfileMutation.isPending}
                className="gap-1.5"
              >
                <Save className="w-4 h-4" />
                {saveProfileMutation.isPending ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
              </Button>
            </TabsContent>

            <TabsContent value="password" dir="rtl" className="space-y-5">
              <div className="space-y-2">
                <Label>رمز عبور جدید</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label>تکرار رمز عبور جدید</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  dir="ltr"
                />
              </div>
              {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
              {passwordSuccess && <p className="text-sm text-emerald-400">{passwordSuccess}</p>}
              <Button
                onClick={() => changePasswordMutation.mutate()}
                disabled={changePasswordMutation.isPending || !newPassword}
                className="gap-1.5"
              >
                <KeyRound className="w-4 h-4" />
                {changePasswordMutation.isPending ? 'در حال تغییر...' : 'تغییر رمز عبور'}
              </Button>
            </TabsContent>

            <TabsContent value="preferences" dir="rtl" className="space-y-5">
              <div className="bg-card border border-border rounded-xl p-5 space-y-4">
                <div className="space-y-2">
                  <Label>زبان</Label>
                  <Input value="فارسی" disabled />
                  <p className="text-xs text-muted-foreground">در نسخه ۱ فقط فارسی پشتیبانی می‌شود</p>
                </div>
                <div className="space-y-2">
                  <Label>تقویم پیش‌فرض</Label>
                  <Select value={defaultCalendar} onValueChange={setDefaultCalendar}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="jalali">شمسی (جلالی)</SelectItem>
                      <SelectItem value="gregorian">میلادی</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="gap-1.5">
                <Save className="w-4 h-4" />
                ذخیره ترجیحات
              </Button>
            </TabsContent>

          </div>
        </div>
      </Tabs>
    </div>
  )
}
